// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { coverageTone } from '../../services/sopGovernanceSelectors';
import type { EquipmentTypeGap, SiteCoverageRow } from '../../types/sopGovernance';
import { equipmentTypeLabel } from './formatters';
import CoverageBar from './CoverageBar';
import { MissingValue } from '../common/MissingValue';

/** Why a row's binding figures are absent. */
const NO_BINDING = 'the Digital Twin publishes no hasSOP attribute on equipment';

/** `2 × Generator set · 1 × Switchgear`, joined here rather than in the model. */
function outstandingText(outstanding: EquipmentTypeGap[]): string {
  return outstanding
    .map(gap => `${gap.count} × ${equipmentTypeLabel(gap.equipmentType)}`)
    .join(' · ');
}

/**
 * Per-site coverage table.
 *
 * A row survives a missing numerator. The twin returning equipment but no
 * `hasSOP` still tells us the site exists and how much bindable equipment it
 * has, so the row keeps its place and marks the bound count, the bar and the gap
 * column as unpublished — the table never silently loses sites.
 */
export default function CoverageBySite({ sites }: { sites: SiteCoverageRow[] }) {
  return (
    <div className="sg-panel">
      <table className="sg-table sg-table--sites">
        <thead>
          <tr>
            <th scope="col">Site</th>
            <th scope="col">Coverage</th>
            <th scope="col">Gaps</th>
          </tr>
        </thead>
        <tbody>
          {sites.map(site => (
            <tr key={site.siteId}>
              <td>
                <div className="sg-site-name">{site.label}</div>
                <div className="sg-site-count">
                  {site.bound === null ? <MissingValue reason={NO_BINDING} /> : site.bound}/
                  {site.total} bound
                </div>
              </td>
              <td>
                <CoverageBar
                  pct={site.pct}
                  tone={site.pct === null ? 'neutral' : coverageTone(site.pct)}
                  missingReason={NO_BINDING}
                  withLabel
                />
              </td>
              {site.outstanding === null ? (
                <td className="sg-gap-unknown">Not measured — {NO_BINDING}</td>
              ) : site.outstanding.length === 0 ? (
                <td className="sg-gap-none">Full coverage</td>
              ) : (
                <td className="sg-gap-text">{outstandingText(site.outstanding)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
